/**
 * Helpers for dealing with the signed upload URLs returned by
 * `ApiService.uploadUrlsCreate`.
 *
 * GCS signed upload URLs have the shape:
 *   https://{bucket}/{optional-path-segments}/{file_name}?X-Goog-...
 *
 * Consumers need either:
 *   - the ({ bucket, file_name }) pair to report back to the backend, or
 *   - the public download URL (https://storage.googleapis.com/{bucket}/{file_name})
 *     to hand off to a downstream task (e.g. the voice-cloning job or a
 *     Document extraction).
 *
 * Centralising this logic means changes to the signed URL structure (e.g.
 * a different storage provider, a new path layout) are a one-file update.
 */

export type GcsUploadLocation = {
    bucket: string;
    file_name: string;
};

/**
 * Parses a signed GCS upload URL into its bucket and file_name components.
 * The bucket is the URL host; the file_name is the last path segment
 * (URI-decoded).
 */
export function parseGcsUploadLocation(uploadUrl: string): GcsUploadLocation {
    const url = new URL(uploadUrl);
    const lastSegment = url.pathname
        .split('/')
        .filter((segment) => segment)
        .pop();
    const file_name = decodeURIComponent(lastSegment ?? '');
    return {
        bucket: url.host,
        file_name,
    };
}

/**
 * Builds the public GCS download URL from a bucket and file_name pair.
 */
function buildPublicGcsUrl({ bucket, file_name }: GcsUploadLocation): string {
    return `https://storage.googleapis.com/${bucket}/${file_name}`;
}

/**
 * Convenience: parse a signed upload URL and return the matching public
 * download URL. Pass `fileName` to use a known filename instead of parsing
 * the pathname (preferred when the API response already exposes it).
 */
export function buildPublicGcsUrlFromUploadUrl(uploadUrl: string, fileName?: string): string {
    const parsed = parseGcsUploadLocation(uploadUrl);
    const file_name = fileName ?? parsed.file_name;
    return buildPublicGcsUrl({ bucket: parsed.bucket, file_name });
}
